import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Title,
  Paper,
  Text,
  Grid,
  Badge,
  Button,
  Group,
  Loader,
  Center,
  Divider,
} from '@mantine/core';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import { PersonaDetectionsSection } from '../components/PersonaDetectionsSection';

export function PersonaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchPersona = useStore((state) => state.fetchPersona);
  const selectedPersona = useStore((state) => state.selectedPersona);
  const personasLoading = useStore((state) => state.personasLoading);

  const fetchPersonaDetections = useStore((state) => state.fetchPersonaDetections);
  const personaDetections = useStore((state) => state.personaDetections);
  const personaDetectionsCount = useStore((state) => state.personaDetectionsCount);
  const personaDetectionsLoading = useStore((state) => state.personaDetectionsLoading);
  const clearPersonaDetections = useStore((state) => state.clearPersonaDetections);

  useEffect(() => {
    if (id) {
      fetchPersona(id);
      fetchPersonaDetections(id);
    }

    return () => {
      clearPersonaDetections();
    };
  }, [id, fetchPersona, fetchPersonaDetections, clearPersonaDetections]);

  if (personasLoading || !selectedPersona) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'gray';
      default:
        return 'blue';
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Button
          variant="subtle"
          leftSection={<ArrowLeft size={16} />}
          onClick={() => navigate('/personas')}
        >
          Back
        </Button>
      </Group>

      <Title order={1}>Persona Details</Title>

      <Paper withBorder p="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              Persona ID
            </Text>
            <Text size="sm" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {selectedPersona.persona_id}
            </Text>
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              Device Count
            </Text>
            <Text size="sm">
              {selectedPersona.device_count} devices
            </Text>
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              Status
            </Text>
            <Badge color={getStatusColor(selectedPersona.status)} mt="xs">
              {selectedPersona.status}
            </Badge>
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              First Seen
            </Text>
            <Text size="sm">
              {format(new Date(selectedPersona.first_seen), 'MMMM d, yyyy HH:mm:ss')}
            </Text>
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              Last Seen
            </Text>
            <Text size="sm">
              {format(new Date(selectedPersona.last_seen), 'MMMM d, yyyy HH:mm:ss')}
            </Text>
          </Grid.Col>

          <Grid.Col span={{ base: 6, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              Observation Count
            </Text>
            <Text size="sm">{selectedPersona.observation_count}</Text>
          </Grid.Col>
        </Grid>
      </Paper>

      {selectedPersona.rssi_correlation && (
        <Paper withBorder p="md">
          <Title order={4} mb="md">
            RSSI Correlation
          </Title>
          <Grid>
            {Object.entries(selectedPersona.rssi_correlation).map(([key, value]) => (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={key}>
                <Text size="sm" c="dimmed">
                  {key}
                </Text>
                <Text size="sm" fw={500}>
                  {typeof value === 'number' ? value.toFixed(2) : value}
                </Text>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>
      )}

      <Divider my="lg" />

      {id && (
        <PersonaDetectionsSection
          personaId={id}
          detections={personaDetections}
          totalCount={personaDetectionsCount}
          loading={personaDetectionsLoading}
          onRefresh={() => fetchPersonaDetections(id)}
        />
      )}
    </Stack>
  );
}
